export type WaveformType = 'sine' | 'square' | 'triangle' | 'sawtooth';

export interface Wave {
    id: number;
    freq: number;
    amp: number; // 0-100
    phase: number; // 0-360
    color: string;
    waveform?: WaveformType; // Defaults to sine for backward compatibility
    muted?: boolean;
    channel?: 'left' | 'right' | 'both'; // Audio channel assignment
    autoPanRate?: number; // Auto-pan LFO rate in Hz (0 = disabled, 0.5-2 Hz typical for EMDR)
    autoPanDepth?: number; // Auto-pan depth 0-100 (100 = full L-R sweep)
    originalFreq?: number; // Original audio frequency in Hz (for display when different from visual freq)
}

export interface FavoriteFrequency {
    id: string;
    name: string;
    freq: number;
    amp: number;
    phase: number;
    color: string;
    waveform?: WaveformType;
    channel?: 'left' | 'right' | 'both';
    autoPanRate?: number;
    autoPanDepth?: number;
    createdAt: number;
}

export interface DFTCoefficient {
    freq: number;
    amp: number;
    phase: number;
}

export type PresetType = 'square' | 'saw' | 'clear';

export interface Song {
    id: string;
    name: string;
    fileName: string;
    storagePath: string;
    downloadUrl: string;
    duration: number;
    size: number;
    addedAt: number;
    // Metadata fields
    artist?: string | null;
    album?: string | null;
    albumArtist?: string | null;
    genre?: string | null;
    year?: number | null;
    trackNumber?: number | null;
    discNumber?: number | null;
    composer?: string | null;
    comment?: string | null;
    // Audio analysis
    analysis?: {
        spectralProfile: number[]; // 24 values representing frequency bands (20Hz-20kHz)
        dominantFreqs: number[];   // Top 5 dominant frequencies
        energy: { 
            bass: number;    // 20-250Hz energy (0-1)
            mid: number;     // 250-4kHz energy (0-1)
            treble: number;  // 4-20kHz energy (0-1)
        };
        bpm?: number;        // Detected tempo (if applicable)
        key?: string;        // Musical key (if detectable)
    };
}

export interface Playlist {
    id: string;
    name: string;
    songIds: string[];
    createdAt: number;
}

export type UploadStatus = 
    | 'pending'      // Waiting in queue
    | 'checking'     // Running duplication check
    | 'duplicate'    // Already exists in library
    | 'uploading'    // Currently uploading
    | 'analyzing'    // Analyzing audio content
    | 'completed'    // Successfully uploaded
    | 'error';       // Failed with error

export interface UploadQueueItem {
    id: string;
    file: File;
    status: UploadStatus;
    progress: number;
    error?: string;
    songName: string;
    duration?: number;
}

export const COLORS = [
    '#3B82F6', // Blue
    '#EF4444', // Red
    '#10B981', // Emerald
    '#F59E0B', // Amber
    '#8B5CF6', // Violet
    '#EC4899', // Pink
    '#06B6D4', // Cyan
    '#84CC16'  // Lime
];

// Theme Types
export type ThemeMode = 'light' | 'dark' | 'auto';

export interface Theme {
    mode: ThemeMode;
    colors: {
        // Background colors
        background: string;
        backgroundSecondary: string;
        backgroundTertiary: string;
        
        // Text colors
        text: string;
        textSecondary: string;
        textMuted: string;
        
        // Border colors
        border: string;
        borderLight: string;
        
        // Interactive colors
        primary: string;
        primaryHover: string;
        secondary: string;
        secondaryHover: string;
        
        // Status colors
        success: string;
        warning: string;
        error: string;
        info: string;
        
        // Component specific
        cardBackground: string;
        modalBackground: string;
        modalOverlay: string;
        inputBackground: string;
        buttonBackground: string;
        buttonText: string;
        
        // Shadows
        shadowSm: string;
        shadowMd: string;
        shadowLg: string;
    };
}
